import { Menu } from 'lucide-react';
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from './drawer';
import { Skeleton } from './skeleton';
import { cn } from '@/utils/utils.ts';

export const MNavbar = ({ children }: { children: React.ReactNode }) => {
  return (
    <nav className="fixed bottom-0 z-40 w-full px-2 pb-2 md:bottom-[none] md:top-0 md:pb-0 md:pt-2">
      <div className="flex h-14 items-center gap-6 rounded-md bg-gray-50 px-4 outline outline-2 outline-gray-100">
        {children}
      </div>
    </nav>
  );
};

export const MNavbarLogo = ({ children }: { children: React.ReactNode }) => {
  return <div>{children}</div>;
};

export const MNavbarList = ({
  children,
  isLoading,
}: {
  children: React.ReactNode;
  isLoading?: boolean;
}) => {
  if (isLoading)
    return (
      <div className="flex flex-1 justify-end gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton
            key={index}
            className="flex size-8 items-center justify-center rounded-lg"
          />
        ))}
      </div>
    );

  return <ul className="flex flex-1 justify-end gap-2">{children}</ul>;
};

export const MNavbarItem = ({
  icon,
  active,
  alert = false,
}: {
  icon: React.ReactElement;
  active?: boolean;
  alert?: boolean;
}) => {
  return (
    <li
      className={`flex items-center justify-center rounded-lg p-1 ${
        active ? 'bg-slate-200' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {icon}

      {alert && <div className={`absolute right-2 top-2 h-2 w-2 rounded bg-slate-400`} />}
    </li>
  );
};

export const MNavbarMenu = ({
  children,
  isLoading,
}: {
  children: React.ReactNode;
  isLoading?: boolean;
}) => {
  if (isLoading)
    return <Skeleton className="flex size-8 items-center justify-center rounded-lg" />;

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <button className="text-gray-600">
          <Menu />
          <span className="sr-only">Toggle Menu</span>
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <ul className="flex flex-col gap-4 px-4 pb-4 pt-6">{children}</ul>
      </DrawerContent>
    </Drawer>
  );
};

export const MNavbarMenuCategoryList = ({
  children,
  category,
}: {
  children: React.ReactNode;
  category: string;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <span className="rounded-md text-sm font-semibold tracking-wider text-neutral-400">
        {category}
      </span>
      <ul className="flex flex-col gap-2">{children}</ul>
    </div>
  );
};

export const MNavbarMenuItem = ({
  text,
  icon,
  active,
  alert = false,
  className,
}: {
  text: string;
  icon: React.ReactElement;
  active?: boolean;
  alert?: boolean;
  className?: string;
}) => {
  return (
    <DrawerClose asChild>
      <li
        className={cn(
          `flex items-center gap-2 rounded-lg p-1 ${
            active ? 'bg-slate-200' : 'text-gray-600 hover:bg-gray-100'
          }`,
          className
        )}
      >
        {icon}
        <span className="font-semibold">{text}</span>

        {alert && <div className={`absolute right-2 top-2 h-2 w-2 rounded bg-green-400`} />}
      </li>
    </DrawerClose>
  );
};
